import { useEffect, useRef, useState } from 'react';

const stats = [
  {
    icon: 'ri-store-2-line',
    value: 1800,
    suffix: '+',
    label: '已服务跨境企业',
    sublabel: '覆盖亚马逊、TikTok Shop、独立站等主流平台',
    color: 'from-[#14B8A6] to-[#0D9488]',
  },
  {
    icon: 'ri-file-list-3-line',
    value: 80,
    suffix: '万+',
    label: '单证归档处理量',
    sublabel: '每一笔都可追溯、可审计、可导出',
    color: 'from-[#FF6B35] to-[#FF8C42]',
  },
  {
    icon: 'ri-time-line',
    value: 3,
    suffix: '天',
    label: '平均上手周期',
    sublabel: '小团队无需专职财务，快速跑通全流程',
    color: 'from-[#14B8A6] to-[#0D9488]',
  },
];

function useCountUp(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const isDecimal = target % 1 !== 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = isDecimal
        ? Math.round(eased * target * 10) / 10
        : Math.floor(eased * target);
      setCount(current);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({
  stat,
  index,
  visible,
}: {
  stat: typeof stats[0];
  index: number;
  visible: boolean;
}) {
  const count = useCountUp(stat.value, 1800, visible);
  const display = stat.value % 1 !== 0 ? count.toFixed(1) : count;

  return (
    <div
      className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-default"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-5`}>
        <i className={`${stat.icon} text-2xl text-white`}></i>
      </div>
      <div className="flex items-end gap-1 mb-2">
        <span className="text-5xl font-bold text-gray-800 leading-none tabular-nums">{display}</span>
        <span className="text-2xl font-bold text-gray-500 mb-1">{stat.suffix}</span>
      </div>
      <div className="text-base font-semibold text-gray-700 mb-1">{stat.label}</div>
      <div className="text-sm text-gray-400 leading-relaxed">{stat.sublabel}</div>
    </div>
  );
}

export default function SocialProof() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-[#F0FDFA] to-white">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#14B8A6]/10 text-[#0D9488] text-sm font-medium px-4 py-2 rounded-full mb-4">
            <i className="ri-verified-badge-line text-base"></i>
            真实数据，持续更新
          </div>
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            已服务{' '}
            <span className="bg-gradient-to-r from-[#0D9488] to-[#14B8A6] bg-clip-text text-transparent">
              1800+
            </span>{' '}
            家跨境企业
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            从小团队到规模卖家，星贸云帮助他们把 9810 合规从"头疼事"变成"标准动作"
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-3 gap-6">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}
